import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Camera, Mic, Square, Plus, X, Copy, Check, CheckCheck, User as UserIcon, AlertTriangle, Wrench,
} from "lucide-react";
import PhotoLightbox from "@/components/PhotoLightbox";
import { derivedStatus, STATUS_LABEL, STATUS_COLOR } from "@/lib/unitStatus";
import { compressImage } from "@/lib/imageCompress";

const STATUS_META = {
  // Kept only for backward-compat references — primary status rendering uses
  // STATUS_LABEL / STATUS_COLOR from @/lib/unitStatus.
  pending: { cls: "badge-slate", label: "Aloittamatta" },
  in_progress: { cls: "badge-amber", label: "Kesken" },
  done: { cls: "badge-emerald", label: "Valmis" },
  inaccessible: { cls: "badge-red", label: "Estynyt" },
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Returns true if viewport is mobile (<768px). Updates live on resize/orientation.
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
}

/**
 * Debounced text field that owns its own state and saves on idle.
 *
 * Typing-bug fix:
 *  - Local state is the source of truth while the user is dirty.
 *  - Prop -> local sync ONLY runs when the user is NOT mid-edit
 *    AND the incoming prop value differs from what we last saw.
 *  - This prevents stale parent re-renders from clobbering the input
 *    mid-keystroke (the "letters disappear / input bounces" symptom).
 */
function DebouncedTextarea({ value, onSave, rows = 2, placeholder, className = "", testId }) {
  const [local, setLocal] = useState(value || "");
  const [savingState, setSavingState] = useState("idle"); // idle | saving | saved
  const timerRef = useRef(null);
  const dirtyRef = useRef(false);
  const lastPropRef = useRef(value || "");

  useEffect(() => {
    const incoming = value || "";
    if (incoming === lastPropRef.current) return;
    lastPropRef.current = incoming;
    if (dirtyRef.current) return; // user is typing — never override
    setLocal(incoming);
  }, [value]);

  const onChange = (e) => {
    const next = e.target.value;
    dirtyRef.current = true;
    setLocal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    setSavingState("saving");
    timerRef.current = setTimeout(async () => {
      try {
        await onSave(next);
        lastPropRef.current = next;
        setSavingState("saved");
        setTimeout(() => setSavingState("idle"), 800);
      } catch {
        setSavingState("idle");
      } finally {
        dirtyRef.current = false;
      }
    }, 600);
  };

  return (
    <div className="relative">
      <textarea
        rows={rows}
        value={local}
        onChange={onChange}
        placeholder={placeholder}
        className={`input-base ${className}`}
        data-testid={testId}
      />
      <div className="absolute top-2 right-3 text-[10px] uppercase tracking-wider font-semibold pointer-events-none">
        {savingState === "saving" && <span className="text-slate-400">Tallennetaan…</span>}
        {savingState === "saved" && <span className="text-emerald-600">Tallennettu</span>}
      </div>
    </div>
  );
}

function DebouncedInput({ value, onSave, placeholder, testId }) {
  const [local, setLocal] = useState(value || "");
  const timerRef = useRef(null);
  const dirtyRef = useRef(false);
  const lastPropRef = useRef(value || "");

  useEffect(() => {
    const incoming = value || "";
    if (incoming === lastPropRef.current) return;
    lastPropRef.current = incoming;
    if (dirtyRef.current) return;
    setLocal(incoming);
  }, [value]);

  const change = (next) => {
    dirtyRef.current = true;
    setLocal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        await onSave(next);
        lastPropRef.current = next;
      } finally {
        dirtyRef.current = false;
      }
    }, 600);
  };

  return (
    <input
      value={local}
      onChange={(e) => change(e.target.value)}
      placeholder={placeholder}
      className="input-base"
      data-testid={testId}
    />
  );
}

export default function UnitCard({ report, unit, onUpdate, onCopyDescription, onReportRefresh, onRequestMarkDone, onOpenCamera, defaultExpanded = false }) {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [newChecklistText, setNewChecklistText] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recordTarget, setRecordTarget] = useState("notes");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // --- Tap/Swipe detection for header (single-tap = expand, double-tap = camera, swipe-left = camera) ---
  const tapTimerRef = useRef(null);
  const lastTapRef = useRef(0);
  const swipedRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0 });

  useEffect(() => () => {
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
  }, []);

  const fireExpandToggle = () => setExpanded((e) => !e);
  const fireCamera = () => onOpenCamera?.(unit);

  const handleHeaderClick = () => {
    if (swipedRef.current) {
      swipedRef.current = false;
      return; // suppress click that came from a swipe gesture
    }
    if (!onOpenCamera) {
      // No double-tap behavior wired — just toggle immediately
      fireExpandToggle();
      return;
    }
    const now = Date.now();
    if (now - lastTapRef.current < 280) {
      // Double tap/click — open camera
      if (tapTimerRef.current) {
        clearTimeout(tapTimerRef.current);
        tapTimerRef.current = null;
      }
      lastTapRef.current = 0;
      fireCamera();
    } else {
      // Wait briefly to see if a second tap arrives
      lastTapRef.current = now;
      tapTimerRef.current = setTimeout(() => {
        lastTapRef.current = 0;
        tapTimerRef.current = null;
        fireExpandToggle();
      }, 280);
    }
  };

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    if (onOpenCamera && dx < -50 && Math.abs(dy) < 40) {
      swipedRef.current = true; // suppress click that follows touchend
      fireCamera();
    }
  };

  const sm = STATUS_META[unit.status] || STATUS_META.pending;
  void sm;
  const checkedCount = (unit.checklist || []).filter((c) => c.checked).length;
  const photosCount = (unit.photos || []).length;
  const materialsCount = (unit.materials_used || []).length;
  const findingsCount =
    (unit.photos || []).filter((p) => p.is_finding).length +
    (unit.checklist || []).filter((c) => c.is_finding && c.checked).length +
    ((unit.additional_work_text || "").trim() ? 1 : 0);
  const hasDeficiency = !!(unit.additional_work_text || "").trim();

  const setStatus = (status) => onUpdate({ status });

  const toggleCheck = (id) => {
    const next = (unit.checklist || []).map((c) => (c.id === id ? { ...c, checked: !c.checked } : c));
    onUpdate({ checklist: next });
  };

  const addCustomCheck = () => {
    const txt = newChecklistText.trim();
    if (!txt) return;
    const item = { id: Math.random().toString(36).slice(2, 10), label: txt, checked: true, custom: true };
    onUpdate({ checklist: [...(unit.checklist || []), item] });
    setNewChecklistText("");
  };

  const removeCheck = (id) => {
    onUpdate({ checklist: (unit.checklist || []).filter((c) => c.id !== id) });
  };

  const startRecording = async (target) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      setRecordTarget(target);
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        const fd = new FormData();
        fd.append("file", blob, "note.webm");
        fd.append("language", "fi");
        try {
          const { data } = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = data.text?.trim();
          if (text) {
            const current = unit[target] || "";
            const next = current ? current + " " + text : text;
            onUpdate({ [target]: next });
            toast.success("Puhe litteroitu");
          }
        } catch {
          toast.error("Litterointi epäonnistui");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Mikrofonia ei saatu käyttöön");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      let lastReport = null;
      for (const file of files) {
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append("file", compressed, file.name);
        fd.append("caption", "");
        const { data } = await api.post(`/reports/${report.report_id}/units/${unit.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.report) lastReport = data.report;
      }
      toast.success(`${files.length} kuva${files.length > 1 ? "a" : ""} ladattu`);
      if (lastReport) onReportRefresh?.(lastReport);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kuvan lataus epäonnistui");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white print-unit-card" data-testid={`unit-card-${unit.id}`}>
      <button
        type="button"
        onClick={handleHeaderClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="w-full flex items-center gap-3 p-3 md:p-4 text-left hover:bg-slate-50 transition-colors select-none touch-pan-y"
        data-testid={`unit-toggle-${unit.id}`}
      >
        <div className="text-slate-400">
          {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>
        {/* Status dot — quick visual cue when scanning the unit list */}
        <span
          className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${STATUS_COLOR[derivedStatus(unit)].dot}`}
          aria-label={STATUS_LABEL[derivedStatus(unit)]}
          data-testid={`unit-status-dot-${unit.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-slate-900 text-base md:text-lg truncate">{unit.label}</div>
          {/* Rauhallinen tekstirivi — ei värillisiä badgeja. Näytetään vain
              lukemat joilla on arvoa (ei nollia). */}
          {(() => {
            const parts = [];
            if (photosCount > 0) parts.push(`${photosCount} ${photosCount === 1 ? "kuva" : "kuvaa"}`);
            if (materialsCount > 0) parts.push(`${materialsCount} ${materialsCount === 1 ? "tarvike" : "tarviketta"}`);
            if (findingsCount > 0) parts.push(`${findingsCount} ${findingsCount === 1 ? "havainto" : "havaintoa"}`);
            if (unit.checklist?.length) parts.push(`${checkedCount}/${unit.checklist.length} kuitattu`);
            if (parts.length === 0) {
              return (
                <div className="text-xs text-slate-400 mt-0.5" data-testid={`unit-summary-${unit.id}`}>Ei vielä sisältöä</div>
              );
            }
            return (
              <div className="text-xs text-slate-500 mt-0.5" data-testid={`unit-summary-${unit.id}`}>
                {parts.join(" · ")}
              </div>
            );
          })()}
        </div>
        <span
          className="ml-1 text-xs font-medium text-slate-500 hover:text-slate-800"
          data-testid={`unit-open-link-${unit.id}`}
        >
          {expanded ? "Sulje" : "Avaa →"}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-5">
          {/* Laitetiedot — raportilla määritetyt kentät. Näytetään heti yksikön avattaessa. */}
          {(report?.device_fields || []).length > 0 && (
            <div data-testid={`unit-device-info-${unit.id}`}>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Laitetiedot</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(report.device_fields || []).map((f) => (
                  <div key={f.id} className="flex items-center gap-2">
                    <label className="text-sm text-slate-600 min-w-[110px] flex-shrink-0">{f.label}</label>
                    <DebouncedInput
                      value={(unit.device_info || {})[f.id] || ""}
                      onSave={(v) => onUpdate({ device_info: { ...(unit.device_info || {}), [f.id]: v } })}
                      placeholder={f.label}
                      testId={`device-info-${unit.id}-${f.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mittauskentät — raportilla määritetyt kentät. */}
          {(report?.measurement_fields || []).length > 0 && (
            <div data-testid={`unit-measurements-${unit.id}`}>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Mittausarvot</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(report.measurement_fields || []).map((f) => (
                  <div key={f.id} className="flex items-center gap-2">
                    <label className="text-sm text-slate-600 min-w-[110px] flex-shrink-0">{f.label}</label>
                    <div className="flex-1 flex items-center gap-2">
                      <DebouncedInput
                        value={(unit.measurements || {})[f.id] || ""}
                        onSave={(v) => onUpdate({ measurements: { ...(unit.measurements || {}), [f.id]: v } })}
                        placeholder="—"
                        testId={`measurement-${unit.id}-${f.id}`}
                      />
                      {f.unit && (
                        <span className="text-xs text-slate-500 font-medium whitespace-nowrap">{f.unit}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* --- Työn kirjaus -umbrella-osio --- */}
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 pt-1">Työn kirjaus</div>

          {/* Toimenpiteet (entinen "Työnkuva") */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-slate-700 inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-emerald-600" /> Tehdyt toimenpiteet
              </label>
              <button
                type="button"
                onClick={() => onCopyDescription(unit)}
                disabled={!(unit.description || "").trim()}
                className="text-xs text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center gap-1 disabled:text-slate-300 disabled:cursor-not-allowed"
                data-testid={`unit-copy-description-${unit.id}`}
                title={!(unit.description || "").trim() ? "Kirjoita ensin toimenpiteet, sitten voit kopioida muille" : "Kopioi nämä toimenpiteet muihin yksiköihin"}
              >
                <Copy className="h-3 w-3" /> Kopioi muihin
              </button>
            </div>
            <DebouncedTextarea
              value={unit.description || ""}
              onSave={(v) => onUpdate({ description: v })}
              placeholder="esim. Hanan vaihto, suodattimet vaihdettu, sähkötarkastus…"
              testId={`unit-description-${unit.id}`}
            />
          </div>

          {/* Työn tarkistuslista — vain is_finding=false. Havainto-checklistit
              renderöidään amber-blokin sisällä lähellä Havainnot-kenttää. */}
          {(!isMobile || (unit.checklist || []).filter((c) => !c.is_finding).length > 0) && (
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-1">Tarkistuslista</div>
            {!isMobile && (
              <p className="text-xs text-slate-500 mb-2">Lisää omat työvaiheet tai kuittaa pohjasta tulleet.</p>
            )}
            <div className="space-y-1.5">
              {(unit.checklist || []).filter((c) => !c.is_finding).map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                    c.checked
                      ? "bg-emerald-50 border-emerald-200"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`check-row-${unit.id}-${c.id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleCheck(c.id)}
                    className={`h-6 w-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                      c.checked ? "bg-emerald-500 border-emerald-500" : "bg-white border-slate-300"
                    }`}
                    data-testid={`check-toggle-${unit.id}-${c.id}`}
                  >
                    {c.checked && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                  </button>
                  <span className={`flex-1 text-sm ${c.checked ? "text-slate-900" : "text-slate-700"}`} onClick={() => toggleCheck(c.id)}>
                    {c.label}
                  </span>
                  {c.custom && !isMobile && (
                    <button
                      type="button"
                      onClick={() => removeCheck(c.id)}
                      className="p-1 rounded text-slate-400 hover:text-red-600"
                      data-testid={`check-remove-${unit.id}-${c.id}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </label>
              ))}
            </div>
            {!isMobile && (
              <div className="mt-2 flex gap-2">
                <input
                  value={newChecklistText}
                  onChange={(e) => setNewChecklistText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomCheck())}
                  placeholder="Lisää oma tarkistuskohta..."
                  className="input-base flex-1"
                  data-testid={`check-new-${unit.id}`}
                />
                <button
                  onClick={addCustomCheck}
                  disabled={!newChecklistText.trim()}
                  className="btn-primary px-4 disabled:opacity-50"
                  data-testid={`check-add-${unit.id}`}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          )}

          {/* Kuvat */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-700">Kuvat</div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  hidden
                  onChange={(e) => { handlePhotoUpload(e.target.files); e.target.value = ""; }}
                  data-testid={`photo-input-${unit.id}`}
                />
                {onOpenCamera && (
                  <button
                    type="button"
                    onClick={() => onOpenCamera(unit)}
                    className="btn-primary text-sm"
                    data-testid={`unit-open-camera-${unit.id}`}
                    title="Nopea kameranäkymä – havainnot ja kuvateksti samalla"
                  >
                    <Camera className="h-4 w-4" /> Ota kuva
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-ghost text-sm"
                  data-testid={`photo-upload-${unit.id}`}
                >
                  <Camera className="h-4 w-4" /> {uploading ? "Ladataan..." : "Lisää kuvia"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 md:gap-2.5">
              {(unit.photos || []).map((p, i) => (
                <button
                  key={p.photo_id}
                  type="button"
                  onClick={() => setLightboxIdx(i)}
                  className="group relative rounded-lg overflow-hidden bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all block w-full focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid={`photo-thumb-${p.photo_id}`}
                >
                  <img
                    src={`${BACKEND}/api/photos/${p.photo_id}`}
                    alt={p.caption || ""}
                    className="w-full aspect-square object-cover bg-slate-50 transition-transform group-hover:scale-[1.01]"
                    data-testid={`photo-img-${p.photo_id}`}
                  />
                  {p.is_finding && (
                    <span
                      className="absolute top-1 left-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-semibold uppercase tracking-wider shadow-sm"
                      data-testid={`photo-finding-badge-${p.photo_id}`}
                    >
                      <AlertTriangle className="h-2.5 w-2.5" />
                    </span>
                  )}
                  {p.caption ? (
                    <div className="px-2 py-1.5 text-[11px] text-slate-700 border-t border-slate-100 text-left">
                      <div className="line-clamp-2 leading-snug">{p.caption}</div>
                    </div>
                  ) : (
                    <div className="px-2 py-1.5 text-[10px] text-slate-400 border-t border-slate-100 text-left italic">
                      Lisää teksti
                    </div>
                  )}
                </button>
              ))}
              {(!unit.photos || unit.photos.length === 0) && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-emerald-600 hover:border-emerald-400 transition-colors"
                  data-testid={`photo-empty-${unit.id}`}
                >
                  <Camera className="h-5 w-5 mb-1" />
                  <span className="text-[11px] font-medium">Lisää kuva</span>
                </button>
              )}
            </div>
          </div>

          {/* Käytetyt tarvikkeet — structured per-unit list */}
          <MaterialsUsedBlock
            unit={unit}
            report={report}
            onUpdate={onUpdate}
          />

          {/* Selkeä visuaalinen erotin "Tehdyt toimenpiteet" ja "Havainnot / jatkotoimenpiteet" välille */}
          <div className="border-t-2 border-amber-200 pt-4">
            <div className={`rounded-2xl p-3 md:p-4 border ${(unit.additional_work_text || "").trim() ? "border-amber-300 bg-amber-50/60" : "border-amber-100 bg-amber-50/20"}`}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-amber-800 inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Havainnot / jatkotoimenpiteet
                </label>
                {!recording ? (
                  <button
                    type="button"
                    onClick={() => startRecording("additional_work_text")}
                    disabled={transcribing}
                    className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"
                    data-testid={`extra-record-${unit.id}`}
                  >
                    <Mic className="h-3 w-3" /> {transcribing && recordTarget === "additional_work_text" ? "Litteroidaan..." : "Sanele"}
                  </button>
                ) : recordTarget === "additional_work_text" ? (
                  <button onClick={stopRecording} className="text-xs text-red-600 font-semibold inline-flex items-center gap-1">
                    <Square className="h-3 w-3" /> Lopeta
                  </button>
                ) : null}
              </div>

              {/* Havainto-tarkistuslista — vain is_finding=true checkit. Sijoitettu
                  TÄHÄN (lähelle vapaa tekstikenttää) jotta havainnot ovat yhdessä paikassa. */}
              {(unit.checklist || []).filter((c) => c.is_finding).length > 0 && (
                <div className="space-y-1.5 mb-2.5" data-testid={`finding-checklist-${unit.id}`}>
                  {(unit.checklist || []).filter((c) => c.is_finding).map((c) => (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        c.checked ? "bg-amber-100 border-amber-300" : "bg-white border-amber-200 hover:border-amber-300"
                      }`}
                      data-testid={`check-row-${unit.id}-${c.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleCheck(c.id)}
                        className={`h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                          c.checked ? "bg-amber-500 border-amber-500" : "bg-white border-amber-300"
                        }`}
                        data-testid={`check-toggle-${unit.id}-${c.id}`}
                      >
                        {c.checked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                      </button>
                      <span className="flex-1 text-sm text-slate-800 flex items-center gap-1.5" onClick={() => toggleCheck(c.id)}>
                        <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0" />
                        {c.label}
                      </span>
                      {c.custom && !isMobile && (
                        <button
                          type="button"
                          onClick={() => removeCheck(c.id)}
                          className="p-1 rounded text-slate-400 hover:text-red-600"
                          data-testid={`check-remove-${unit.id}-${c.id}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </label>
                  ))}
                </div>
              )}

              <DebouncedTextarea
                value={unit.additional_work_text || ""}
                onSave={(v) => onUpdate({ additional_work_text: v })}
                placeholder="Kirjoita havainto tai jatkotoimenpide vapaana tekstinä. Esim. 'Puhallin vaihtokunnossa, suositellaan uusimista' tai 'Ohjausvika havaittu'."
                testId={`unit-additional-${unit.id}`}
              />
            </div>
          </div>

          {/* Lisätiedot — kollapsoituna pohjalla. Sisältää suorittajan + status-painikkeet */}
          <details
            className="group rounded-2xl bg-slate-50/60 border border-slate-200 px-4 py-3"
            data-testid={`unit-extra-${unit.id}`}
          >
            <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-semibold text-slate-700 hover:text-slate-900">
              <span>Lisätiedot <span className="text-slate-400 font-normal text-xs">(suorittaja, tila)</span></span>
              <span className="text-xs text-slate-400 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-3 space-y-4">
              {/* Suorittaja */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5 inline-flex items-center gap-1.5">
                  <UserIcon className="h-3.5 w-3.5 text-slate-400" /> Suorittaja <span className="text-slate-400 font-normal text-xs">(valinnainen)</span>
                </label>
                <DebouncedInput
                  value={unit.executed_by || ""}
                  onSave={(v) => onUpdate({ executed_by: v })}
                  placeholder="esim. Mikko Mallikas"
                  testId={`unit-executor-${unit.id}`}
                />
              </div>

              {/* Status actions — moved out of the way to reduce visual clutter */}
              <div className="grid grid-cols-1 gap-2">
                {unit.status === "done" ? (
                  <button
                    onClick={() => setStatus("in_progress")}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold bg-white border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`unit-undo-done-${unit.id}`}
                  >
                    <Check className="h-4 w-4" /> Peru valmis
                  </button>
                ) : (
                  <button
                    onClick={() => onRequestMarkDone ? onRequestMarkDone(unit) : setStatus("done")}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                    data-testid={`unit-mark-done-${unit.id}`}
                  >
                    <CheckCheck className="h-4 w-4" /> Merkitse valmiiksi
                  </button>
                )}
              </div>
            </div>
          </details>

          {lightboxIdx !== null && (unit.photos || [])[lightboxIdx] && (
            <PhotoLightbox
              photos={unit.photos}
              startIndex={lightboxIdx}
              onClose={() => setLightboxIdx(null)}
              onChanged={(updatedReport) => {
                if (updatedReport) {
                  onReportRefresh?.(updatedReport);
                } else {
                  // Photo deleted — drop from local unit
                  // (handled by lightbox calling onChanged after delete)
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

const BADGE_TONE = {
  slate: "bg-slate-100 text-slate-700",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  muted: "bg-slate-50 text-slate-400",
};

function CompactBadge({ tone = "slate", icon: Icon, count, label, testId }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${BADGE_TONE[tone] || BADGE_TONE.slate}`}
      data-testid={testId}
    >
      <Icon className="h-3 w-3" />
      <span>{count}</span>
      <span className="font-normal opacity-80">{label}</span>
    </span>
  );
}


async function _unused_local_compressImage_keep_for_history(file, maxDim = 1600, quality = 0.82) {
  // moved to /app/frontend/src/lib/imageCompress.js — kept here as a no-op stub
  // so any stale imports won't crash. Safe to delete in a later cleanup.
  return file;
}
// eslint-disable-next-line no-unused-vars
const _x = _unused_local_compressImage_keep_for_history;

// -------------------------------------------------------------------
// MaterialsUsedBlock — per-unit structured list of materials actually used
// Uses report.materials (pikavalinnat) for quick picks, plus free entry.
// Data shape: unit.materials_used = [{name, qty, unit}]
// -------------------------------------------------------------------
function MaterialsUsedBlock({ unit, report, onUpdate }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [unitLabel, setUnitLabel] = useState("kpl");
  const list = Array.isArray(unit.materials_used) ? unit.materials_used : [];
  const picks = Array.isArray(report?.materials) ? report.materials : [];

  const save = (next) => onUpdate({ materials_used: next });

  const addItem = (payload) => {
    const n = (payload?.name || name).trim();
    if (!n) return;
    const q = payload?.qty ?? (Number(qty) || 1);
    const u = (payload?.unit || unitLabel || "kpl").trim() || "kpl";
    const existing = list.findIndex((m) => m.name.toLowerCase() === n.toLowerCase());
    if (existing >= 0) {
      const merged = [...list];
      const currentQty = Number(merged[existing].qty) || 0;
      merged[existing] = { ...merged[existing], qty: currentQty + Number(q), unit: u };
      save(merged);
    } else {
      save([...list, { name: n, qty: Number(q), unit: u }]);
    }
    setName(""); setQty(""); setUnitLabel("kpl");
  };

  const updateRow = (idx, patch) => {
    const next = [...list];
    next[idx] = { ...next[idx], ...patch };
    save(next);
  };
  const removeRow = (idx) => save(list.filter((_, i) => i !== idx));

  const quickPickFor = (p) => {
    // Quick-adds 1 of this pick; user can edit qty after
    addItem({ name: p.name, qty: 1, unit: p.unit || "kpl" });
  };

  return (
    <div data-testid={`unit-materials-used-${unit.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-semibold text-slate-700">Käytetyt tarvikkeet</label>
        <span className="text-xs text-slate-400">{list.length} {list.length === 1 ? "rivi" : "riviä"}</span>
      </div>

      {picks.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {picks.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => quickPickFor(p)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-white border border-blue-200 text-blue-800 hover:bg-blue-50"
              data-testid={`unit-material-pick-${unit.id}-${i}`}
              title={`Lisää 1 ${p.unit || "kpl"}`}
            >
              <Plus className="h-3 w-3" /> {p.name} <span className="text-blue-400 font-normal">({p.unit || "kpl"})</span>
            </button>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {list.map((m, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200" data-testid={`unit-material-row-${unit.id}-${i}`}>
              <span className="flex-1 text-sm text-slate-800 truncate">{m.name}</span>
              <input
                type="number" min="0" step="0.1"
                value={m.qty}
                onChange={(e) => updateRow(i, { qty: e.target.value === "" ? "" : Number(e.target.value) })}
                className="w-16 px-2 py-1 rounded border border-slate-200 text-sm text-right"
                data-testid={`unit-material-qty-${unit.id}-${i}`}
              />
              <input
                value={m.unit || "kpl"}
                onChange={(e) => updateRow(i, { unit: e.target.value })}
                className="w-14 px-2 py-1 rounded border border-slate-200 text-sm"
                data-testid={`unit-material-unit-${unit.id}-${i}`}
              />
              <button onClick={() => removeRow(i)} className="p-1 rounded text-slate-400 hover:text-red-600" data-testid={`unit-material-remove-${unit.id}-${i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-[1fr_70px_60px_auto] gap-1.5">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder="Tarvike"
          className="input-base text-sm"
          data-testid={`unit-material-name-${unit.id}`}
        />
        <input
          type="number" min="0" step="0.1"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          placeholder="Määrä"
          className="input-base text-sm text-right"
          data-testid={`unit-material-newqty-${unit.id}`}
        />
        <input
          value={unitLabel}
          onChange={(e) => setUnitLabel(e.target.value)}
          placeholder="kpl"
          className="input-base text-sm"
          data-testid={`unit-material-newunit-${unit.id}`}
        />
        <button
          type="button"
          onClick={() => addItem()}
          disabled={!name.trim()}
          className="btn-primary text-xs px-3 disabled:opacity-50"
          data-testid={`unit-material-add-${unit.id}`}
        >
          Lisää
        </button>
      </div>
    </div>
  );
}
